import { ContentHubData } from "@app/types/ServerCustomContext";
import { configBrightcove, configContentHub } from "@pub/config";
import { ResponsiveIframe } from "@sphtech/dmg-design-system";

import {
  ResolvedCUEElement,
  ResolvedEmbedCUEElement,
} from "../Article/elements";
import { getParsedUrl } from "../Article/StorylineElements/utils";

export function getCoverCrop(coverImage?: ContentHubData["coverImage"]) {
  const { featuredImageCrop, featuredImageAlwaysOriginal } =
    configContentHub.configMasthead;

  const crop = featuredImageAlwaysOriginal
    ? "original"
    : featuredImageCrop || "square_30_26";

  return coverImage?.crops[crop] || coverImage?.crops.original;
}

export function getCoverIframeProps(
  coverEmbed?: ResolvedEmbedCUEElement,
): React.ComponentProps<typeof ResponsiveIframe> | undefined {
  if (!coverEmbed) {
    return;
  }
  let src = coverEmbed.additionalFields.uri?.value;
  if (!src) {
    return;
  }
  let forceAspectRatio;
  if (
    coverEmbed.additionalFields.width?.value &&
    coverEmbed.additionalFields.height?.value
  ) {
    forceAspectRatio =
      +coverEmbed.additionalFields.width.value /
      +coverEmbed.additionalFields.height.value;
  }
  if (
    configBrightcove.bgLoopPlayerId &&
    // ensure src point to our brightcove account
    src.startsWith(
      `https://players.brightcove.net/${configBrightcove.accountId}/`,
    )
  ) {
    const url = getParsedUrl(src);
    if (url) {
      // force usage of bg loop player
      url.pathname = `/${configBrightcove.accountId}/${configBrightcove.bgLoopPlayerId}_default/index.html`;
      src = url.toString();
    }
  }
  return {
    src,
    forceAspectRatio,
  };
}

/**
 * Checks if a CUE element is valid for rendering in the main body content.
 * This filters out elements that are empty or do not meet specific display criteria.
 */
export const isValidBodyElement = (element: ResolvedCUEElement): boolean => {
  switch (element.type) {
    case "paragraph":
      return !!element.value;

    case "sub_head":
      return !!element.additionalFields.subhead?.value;

    case "image": {
      // It should NOT be a sponsor image
      const hasCorrectAnchor =
        element.additionalFields.htmlAnchor?.value !==
        "contenthub-sponsor-image";
      return !!element.relation && hasCorrectAnchor;
    }

    case "embed":
      return !!element.additionalFields.uri?.value;

    case "column_images": {
      const hasChildren =
        Array.isArray(element.children) && element.children.length > 0;
      const hasCorrectAnchor =
        element.additionalFields.htmlAnchor?.value === "content-column-images";
      return hasChildren && hasCorrectAnchor;
    }

    case "list_bulleted":
    case "list_numbered":
      return Array.isArray(element.children) && element.children.length > 0;

    default:
      return false;
  }
};
